import type { AttachmentUpload, ConversationSummary, Grounding, TokenUsage, ToolCallSummary } from "@maimu/contracts";
import { reactive, ref } from "vue";

import { api } from "@/lib/api.js";

export interface ChatTurn {
  id: string;
  role: "user" | "assistant";
  content: string;
  streaming: boolean;
  grounding: Grounding | null;
  usage: TokenUsage | null;
  toolCalls: (ToolCallSummary & { running?: boolean })[];
  /** Filenames sent with this turn, shown back so the user sees what rode along. */
  attachmentNames?: string[];
  error?: string;
}

let localId = 0;
const nextId = (): string => `local-${(localId += 1)}`;

export function useChat() {
  const turns = ref<ChatTurn[]>([]);
  const conversations = ref<ConversationSummary[]>([]);
  const conversationId = ref<string | null>(null);
  /** Set to open the next new thread inside a toimik (D21). */
  const caseId = ref<string | null>(null);
  const caseTitle = ref<string | null>(null);
  const streaming = ref(false);
  const status = ref("");
  const error = ref("");

  let controller: AbortController | null = null;

  async function refreshConversations(): Promise<void> {
    try {
      conversations.value = await api.conversations();
    } catch {
      // The history list is a convenience; a failure here must not block asking.
    }
  }

  function startNew(): void {
    conversationId.value = null;
    caseTitle.value = null;
    turns.value = [];
    error.value = "";
    status.value = "";
  }

  async function open(id: string): Promise<void> {
    const detail = await api.conversation(id);
    conversationId.value = detail.id;
    // An opened thread carries its own toimik, so the banner and the filing control
    // reflect where this conversation actually lives rather than where the last one did.
    caseId.value = detail.caseId ?? null;
    caseTitle.value = detail.caseTitle ?? null;
    error.value = "";
    turns.value = detail.messages.map((message) => ({
      id: message.id,
      role: message.role,
      content: message.content,
      streaming: false,
      grounding: message.grounding,
      usage: message.usage,
      toolCalls: message.toolCalls,
      ...(message.attachments?.length ? { attachmentNames: message.attachments.map((file) => file.name) } : {}),
    }));
  }

  async function send(text: string, attachments: AttachmentUpload[] = []): Promise<void> {
    const question = text.trim();
    if (!question || streaming.value) return;

    error.value = "";
    turns.value.push({
      id: nextId(),
      role: "user",
      content: question,
      streaming: false,
      grounding: null,
      usage: null,
      toolCalls: [],
      // Shown immediately from the upload, before the server names them back.
      attachmentNames: attachments.map((file) => file.name),
    });

    // reactive() before the push, not a plain object. `turns` is a deep ref, so the
    // template reads array elements through a lazily-created proxy — while every
    // mutation below (`content +=`, `toolCalls.push`, the final `streaming = false`)
    // would go to the raw object, whose writes no proxy is watching. The answer would
    // arrive, be persisted, and never appear: the bubble stays on "Mõtlen…" forever.
    const assistant: ChatTurn = reactive({
      id: nextId(),
      role: "assistant",
      content: "",
      streaming: true,
      grounding: null,
      usage: null,
      toolCalls: [],
    });
    turns.value.push(assistant);

    streaming.value = true;
    status.value = "Mõtlen…";
    controller = new AbortController();

    try {
      const body = {
        ...(conversationId.value
          ? { conversationId: conversationId.value }
          : { ...(caseId.value ? { caseId: caseId.value } : {}) }),
        message: question,
        ...(attachments.length ? { attachments } : {}),
      };

      for await (const event of api.chat(body, controller.signal)) {
        switch (event.type) {
          case "message.start":
            conversationId.value = event.conversationId;
            assistant.id = event.assistantMessageId;
            break;

          case "token":
            assistant.content += event.text;
            status.value = "Kirjutan vastust…";
            break;

          case "tool.start":
            assistant.toolCalls.push({
              id: event.id,
              tool: event.tool,
              label: event.label,
              args: event.args,
              ok: false,
              durationMs: 0,
              running: true,
            });
            status.value = event.label;
            break;

          case "tool.end": {
            const index = assistant.toolCalls.findIndex((call) => call.id === event.call.id);
            const finished = { ...event.call, running: false };
            if (index >= 0) assistant.toolCalls[index] = finished;
            else assistant.toolCalls.push(finished);
            break;
          }

          case "grounding":
            assistant.grounding = event.grounding;
            break;

          case "message.end":
            assistant.grounding = event.grounding;
            assistant.usage = event.usage;
            break;

          case "error":
            assistant.error = event.message;
            error.value = event.message;
            break;
        }
      }
    } catch (streamError) {
      if (!(streamError instanceof DOMException && streamError.name === "AbortError")) {
        const message = streamError instanceof Error ? streamError.message : "Midagi läks päringuga valesti.";
        assistant.error = message;
        error.value = message;
      }
    } finally {
      assistant.streaming = false;
      streaming.value = false;
      status.value = "";
      controller = null;
      void refreshConversations();
    }
  }

  function stop(): void {
    controller?.abort();
  }

  return {
    turns,
    conversations,
    conversationId,
    caseId,
    caseTitle,
    streaming,
    status,
    error,
    send,
    stop,
    open,
    startNew,
    refreshConversations,
  };
}
